// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { Container, Form, Card, Button, Row, Col, ToggleButton, Spinner, Table, Badge, Alert } from 'react-bootstrap';
import { BoxArrowUpRight, Download, CloudDownload } from 'react-bootstrap-icons';
import { useGetAllPromptsQuery, useGetPromptQuery } from '../../services/prompts.service';
import { useGetSettingQuery } from '../../services/settings.service';
import { useGenerateTextMutation, useGenerateStructuredMovieRecommendationsMutation, StructuredInspirationResponse } from '../../services/inspiration.service';
import { useGetArtByImdbIdMutation } from '../../services/arts.service';
import { useImportImdbMovieMutation, useBulkImportImdbMoviesMutation } from '../../services/import.service';

interface SandboxFormData {
  model: string;
  imdbNumbers: string;
  systemPromptId: string;
  userPromptId: string;
  usePromptsSequence: boolean;
  sequencePromptId: string;
  useStructuredOutput: boolean;
  output: string;
  structuredOutput: StructuredInspirationResponse | null;
  isGenerating: boolean;
  error: string | null;
}

enum PromptType {
  SYSTEM = 'system',
  USER = 'user',
  SEQUENCE = 'sequence'
}

function SandboxPage(): JSX.Element {
  const [selectedPromptType, setSelectedPromptType] = useState<PromptType | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [existingMovies, setExistingMovies] = useState<Record<string, { exists: boolean; id?: number }>>({});
  const [importingMovies, setImportingMovies] = useState<Record<string, boolean>>({});
  const [bulkImportStatus, setBulkImportStatus] = useState<{ isImporting: boolean; results: any | null }>({ 
    isImporting: false, 
    results: null 
  });
  
  const { data: prompts, isLoading: isLoadingPrompts } = useGetAllPromptsQuery({});
  const { data: selectedPrompt } = useGetPromptQuery(selectedPromptId, { skip: !selectedPromptId });
  const { data: imdbIdsSetting } = useGetSettingQuery('inspiration_sandbox_imdb_ids');
  
  const [generateText, { isLoading: isGeneratingText }] = useGenerateTextMutation();
  const [generateStructuredMovies, { isLoading: isGeneratingStructuredMovies }] = useGenerateStructuredMovieRecommendationsMutation();
  const [getArtByImdbId] = useGetArtByImdbIdMutation();
  const [importImdbMovie] = useImportImdbMovieMutation();
  const [bulkImportImdbMovies] = useBulkImportImdbMoviesMutation();
  
  const [formData, setFormData] = useState<SandboxFormData>({
    model: 'gpt-4o',
    imdbNumbers: '',
    systemPromptId: '',
    userPromptId: '',
    usePromptsSequence: false,
    sequencePromptId: '',
    useStructuredOutput: true,
    output: '',
    structuredOutput: null,
    isGenerating: false,
    error: null
  });

  
  // Update IMDB numbers when the setting is loaded
  useEffect(() => {
    if ((imdbIdsSetting as any)?.value) {
      setFormData(prev => ({
        ...prev,
        imdbNumbers: (imdbIdsSetting as any).value || ''
      }));
    }
  }, [imdbIdsSetting]);

  // Set default prompt options to first available when prompts are loaded
  useEffect(() => {
    if (prompts && (prompts as any).length > 0 && !formData.systemPromptId && !formData.userPromptId) {
      const activePrompts = (prompts as any).filter((p: any) => p.status === 1);
      if (activePrompts.length > 0) {
        const firstSystemPrompt = activePrompts[0].id;
        const firstUserPrompt = activePrompts[0].id;
        const firstSequencePrompt = activePrompts[0].id;
        
        setFormData(prev => ({
          ...prev,
          systemPromptId: firstSystemPrompt,
          userPromptId: firstUserPrompt,
          sequencePromptId: firstSequencePrompt
        }));
        
        // Set the selected prompt type to system initially to show the first prompt
        setSelectedPromptType(PromptType.SYSTEM);
        setSelectedPromptId(firstSystemPrompt);
      }
    }
  }, [prompts, formData.systemPromptId, formData.userPromptId]);

  // Hardcoded list of LLM models - in a real app this would come from an API
  const llmModels = [
    { id: 'gpt-4o', name: 'GPT-4o' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
    { id: 'claude-3-opus', name: 'Claude 3 Opus' },
    { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet' },
    { id: 'claude-3-haiku', name: 'Claude 3 Haiku' },
    { id: 'gemini-pro', name: 'Gemini Pro' },
    { id: 'gemini-ultra', name: 'Gemini Ultra' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleChange = (field: 'usePromptsSequence' | 'useStructuredOutput') => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Check if a movie exists in the database by IMDB number and get its ID
  const checkMovieExists = useCallback(async (imdbNumber: string) => {
    try {
      const response = await getArtByImdbId({ imdbId: imdbNumber }).unwrap();
      return { exists: !!response, id: (response as any)?.id };
    } catch (error) {
      console.error('Error checking if movie exists:', error);
      return { exists: false };
    }
  }, [getArtByImdbId]);

  // Handle importing a movie to the database
  const handleImportMovie = async (imdbNumber: string) => {
    try {
      setImportingMovies(prev => ({ ...prev, [imdbNumber]: true }));
      
      await importImdbMovie(imdbNumber).unwrap();
      
      // Get the movie details after successful import to get its ID
      const movieDetails = await checkMovieExists(imdbNumber);
      setExistingMovies(prev => ({ ...prev, [imdbNumber]: movieDetails }));
    } catch (error) {
      console.error('Error importing movie:', error);
    } finally {
      setImportingMovies(prev => ({ ...prev, [imdbNumber]: false }));
    }
  };

  // Handle bulk importing all recommended movies
  const handleBulkImport = async () => {
    if (!formData.structuredOutput?.recommendations || formData.structuredOutput.recommendations.length === 0) {
      return;
    }

    try {
      // Reset previous results
      setBulkImportStatus({ isImporting: true, results: null });
      
      // Get all IMDB numbers that aren't already in the database
      const imdbNumbersToImport = formData.structuredOutput.recommendations
        .filter(movie => movie.imdbNumber && !existingMovies[movie.imdbNumber])
        .map(movie => movie.imdbNumber);
      
      if (imdbNumbersToImport.length === 0) {
        setBulkImportStatus({ 
          isImporting: false, 
          results: { message: 'All movies are already in the database.' } 
        });
        return;
      }
      
      // Call the bulk import API
      const results = await bulkImportImdbMovies(imdbNumbersToImport).unwrap();
      
      // Update the movie existence status for all imported movies
      const updatedExistingMovies = { ...existingMovies };
      (results as any).forEach((item: { success: boolean; imdbId: string }) => {
        if (item.success) {
          updatedExistingMovies[item.imdbId] = { exists: true };
        }
      });
      
      setExistingMovies(updatedExistingMovies);
      setBulkImportStatus({ isImporting: false, results });
    } catch (error: any) {
      console.error('Error bulk importing movies:', error);
      setBulkImportStatus({ 
        isImporting: false, 
        results: { error: error.message || 'An error occurred during bulk import' } 
      });
    }
  };

  // Update existing movies cache when structured output changes
  useEffect(() => {
    if (formData.structuredOutput?.recommendations) {
      const checkMovies = async () => {
        const movieExistence: Record<string, { exists: boolean; id?: number }> = {};
        
        for (const movie of formData.structuredOutput!.recommendations) {
          if (movie.imdbNumber) {
            movieExistence[movie.imdbNumber] = await checkMovieExists(movie.imdbNumber);
          }
        }
        
        setExistingMovies(movieExistence);
      };
      
      checkMovies();
    }
  }, [formData.structuredOutput, checkMovieExists]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset output and error
    setFormData(prev => ({
      ...prev,
      output: '',
      structuredOutput: null,
      error: null,
      isGenerating: true
    }));
    
    // Reset existing movies cache
    setExistingMovies({});
    
    try {
      // Get the selected prompts content
      const systemPrompt = (prompts as any)?.find((p: any) => p.id === formData.systemPromptId)?.content || '';
      const userPrompt = (prompts as any)?.find((p: any) => p.id === formData.userPromptId)?.content || '';
      const sequencePrompt = formData.usePromptsSequence ? 
        (prompts as any)?.find((p: any) => p.id === formData.sequencePromptId)?.content || '' : '';
      
      // Replace placeholders in prompts
      const processedSystemPrompt = systemPrompt.replace(/\{imdb_ids\}/g, formData.imdbNumbers);
      const processedUserPrompt = userPrompt.replace(/\{imdb_ids\}/g, formData.imdbNumbers);
      const processedSequencePrompt = sequencePrompt.replace(/\{imdb_ids\}/g, formData.imdbNumbers);
      
      // Determine which prompt to use
      let finalUserPrompt = processedUserPrompt;
      if (formData.usePromptsSequence && processedSequencePrompt) {
        finalUserPrompt = `${processedUserPrompt}\n\n${processedSequencePrompt}`;
      }
      
      // Call the appropriate API based on the output type
      if (formData.useStructuredOutput) {
        // Call the structured output API
        const response = await generateStructuredMovies({
          prompt: finalUserPrompt,
          systemPrompt: processedSystemPrompt,
          model: formData.model,
          temperature: 0.7,
          maxTokens: 1000
        }).unwrap();
        
        // Update the form data with the structured response
        setFormData(prev => ({
          ...prev,
          structuredOutput: response as any,
          isGenerating: false
        }));
      } else {
        // Call the regular text API
        const response = await generateText({
          prompt: finalUserPrompt,
          systemPrompt: processedSystemPrompt,
          model: formData.model,
          temperature: 0.7,
          maxTokens: 1000
        }).unwrap();
        
        // Update the form data with the text response
        setFormData(prev => ({
          ...prev,
          output: (response as any).text,
          isGenerating: false
        }));
      }
    } catch (error: any) {
      console.error('Error generating content:', error);
      setFormData(prev => ({
        ...prev,
        error: error.data?.message || 'An error occurred while generating content',
        isGenerating: false
      }));
    }
  };

  return (
    <Container className="mt-4">
      <h2>AI Sandbox</h2>
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Model</Form.Label>
                  <Form.Select 
                    name="model" 
                    value={formData.model}
                    onChange={handleInputChange}
                  >
                    {llmModels.map(model => (
                      <option key={model.id} value={model.id}>{model.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>IMDB Numbers</Form.Label>
                  <Form.Control
                    type="text"
                    name="imdbNumbers"
                    value={formData.imdbNumbers}
                    onChange={handleInputChange}
                    placeholder="e.g. tt0111161, tt0068646, tt0071562"
                  />
                  <Form.Text className="text-muted">
                    Comma separated ids
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <>
                    <Form.Label>System Prompt</Form.Label>
                    <Form.Select
                    name="systemPromptId"
                    value={formData.systemPromptId}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (e.target.value) {
                        setSelectedPromptType(PromptType.SYSTEM);
                        setSelectedPromptId(e.target.value);
                      }
                    }}
                    disabled={isLoadingPrompts}
                  >
                    <option value="">Select a system prompt</option>
                    {(prompts as any)?.filter((p: any) => p.status === 1).map((prompt: any) => (
                      <option key={prompt.id} value={prompt.id}>{prompt.name}</option>
                    ))}
                  </Form.Select>
                  {selectedPromptType === PromptType.SYSTEM && selectedPrompt && (
                    <Card className="mt-2">
                      <Card.Body>
                        <Card.Title>{(selectedPrompt as any).name}</Card.Title>
                        {(selectedPrompt as any).note && <Card.Subtitle className="mb-2 text-muted">{(selectedPrompt as any).note}</Card.Subtitle>}
                        <Card.Text style={{ whiteSpace: 'pre-wrap' }}>{(selectedPrompt as any).content}</Card.Text>
                      </Card.Body>
                    </Card>
                  )}
                  </>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                {/* @ts-ignore */}
                <Form.Group className="mb-3">
                  <Form.Label>User Prompt</Form.Label>
                  <Form.Select
                    name="userPromptId"
                    value={formData.userPromptId}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (e.target.value) {
                        setSelectedPromptType(PromptType.USER);
                        setSelectedPromptId(e.target.value);
                      }
                    }}
                    disabled={isLoadingPrompts}
                  >
                    <option value="">Select a user prompt</option>
                    {(prompts as any)?.filter((p: any) => p.status === 1).map((prompt: any) => (
                      <option key={prompt.id} value={prompt.id}>{prompt.name}</option>
                    ))}
                  </Form.Select>
                  {selectedPromptType === PromptType.USER && selectedPrompt && (
                    <Card className="mt-2">
                      <Card.Body>
                        <Card.Title>{(selectedPrompt as any).name}</Card.Title>
                        {(selectedPrompt as any).note && <Card.Subtitle className="mb-2 text-muted">{(selectedPrompt as any).note}</Card.Subtitle>}
                        <Card.Text style={{ whiteSpace: 'pre-wrap' }}>{(selectedPrompt as any).content}</Card.Text>
                      </Card.Body>
                    </Card>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group className="d-flex align-items-center">
                  <ToggleButton
                    id="toggle-prompts-sequence"
                    type="checkbox"
                    variant="outline-primary"
                    checked={formData.usePromptsSequence}
                    value="1"
                    onChange={() => handleToggleChange('usePromptsSequence')}
                    className="me-2"
                  >
                    {formData.usePromptsSequence ? 'Prompts Sequence: On' : 'Prompts Sequence: Off'}
                  </ToggleButton>
                  <ToggleButton
                    id="toggle-structured-output"
                    type="checkbox"
                    variant="outline-success"
                    checked={formData.useStructuredOutput}
                    value="1"
                    onChange={() => handleToggleChange('useStructuredOutput')}
                    className="ms-2"
                  >
                    {formData.useStructuredOutput ? 'Structured Output: On' : 'Structured Output: Off'}
                  </ToggleButton>
                </Form.Group>
              </Col>
            </Row>

            {formData.usePromptsSequence && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sequence Prompt</Form.Label>
                    <Form.Select
                      name="sequencePromptId"
                      value={formData.sequencePromptId}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (e.target.value) {
                          setSelectedPromptType(PromptType.SEQUENCE);
                          setSelectedPromptId(e.target.value);
                        }
                      }}
                      disabled={isLoadingPrompts}
                    >
                      <option value="">Select a sequence prompt</option>
                      {(prompts as any)?.filter((p: any) => p.status === 1).map((prompt: any) => (
                        <option key={prompt.id} value={prompt.id}>{prompt.name}</option>
                      ))}
                    </Form.Select>
                    {selectedPromptType === PromptType.SEQUENCE && selectedPrompt && (
                      <Card className="mt-2">
                        <Card.Body>
                          <Card.Title>{selectedPrompt.name}</Card.Title>
                          {selectedPrompt.note && <Card.Subtitle className="mb-2 text-muted">{selectedPrompt.note}</Card.Subtitle>}
                          <Card.Text style={{ whiteSpace: 'pre-wrap' }}>{selectedPrompt.content}</Card.Text>
                        </Card.Body>
                      </Card>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            )}

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={isGeneratingText || isGeneratingStructuredMovies || formData.isGenerating || !formData.systemPromptId || !formData.userPromptId}
              >
                {(isGeneratingText || isGeneratingStructuredMovies || formData.isGenerating) ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Generating...
                  </>
                ) : 'Generate'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h4 className="mb-0">Output:</h4>
        </Card.Header>
        <Card.Body>
          <div className="output-container p-3 bg-light rounded">
            {formData.error ? (
              <div className="text-danger">{formData.error}</div>
            ) : formData.isGenerating ? (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-2">Generating response...</p>
              </div>
            ) : formData.structuredOutput ? (
              <div className="structured-output">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Movie Recommendations</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleBulkImport}
                    disabled={bulkImportStatus.isImporting || formData.structuredOutput.recommendations.every(movie => existingMovies[movie.imdbNumber]?.exists)}
                  >
                    {bulkImportStatus.isImporting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Importing...
                      </>
                    ) : (
                      <>
                        <CloudDownload className="me-2" />
                        Bulk Import Missing Movies
                      </>
                    )}
                  </Button>
                </div>
                
                {bulkImportStatus.results && (
                  <Alert 
                    variant={bulkImportStatus.results.error ? "danger" : "success"} 
                    className="mb-3"
                    dismissible
                    onClose={() => setBulkImportStatus(prev => ({ ...prev, results: null }))}
                  >
                    {bulkImportStatus.results.error ? (
                      <p className="mb-0">{bulkImportStatus.results.error}</p>
                    ) : bulkImportStatus.results.message ? (
                      <p className="mb-0">{bulkImportStatus.results.message}</p>
                    ) : (
                      <>
                        <p className="mb-0">
                          <strong>Bulk import completed:</strong> {bulkImportStatus.results.successful} of {bulkImportStatus.results.totalProcessed} movies successfully imported.
                        </p>
                        {bulkImportStatus.results.failed > 0 && (
                          <p className="mb-0 mt-1 text-danger">{bulkImportStatus.results.failed} movies failed to import.</p>
                        )}
                      </>
                    )}
                  </Alert>
                )}
                
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Movie</th>
                      <th>Year</th>
                      <th>Director</th>
                      <th>IMDB</th>
                      <th>Relation</th>
                      <th>Inspiration Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.structuredOutput.recommendations.map((movie, index) => (
                      <tr key={index}>
                        <td><strong>{movie.name}</strong></td>
                        <td>{movie.productionYear}</td>
                        <td>{movie.director}</td>
                        <td>
                          <Badge bg="secondary">{movie.imdbNumber}</Badge>
                          {existingMovies[movie.imdbNumber]?.exists ? (
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="ms-2 p-0" 
                              href={`/arts/view/${existingMovies[movie.imdbNumber]?.id}`} 
                              target="_blank"
                              title="Open movie page"
                            >
                              <BoxArrowUpRight />
                            </Button>
                          ) : (
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="ms-2 p-0" 
                              onClick={() => handleImportMovie(movie.imdbNumber)}
                              disabled={importingMovies[movie.imdbNumber]}
                              title="Import movie to database"
                            >
                              {importingMovies[movie.imdbNumber] ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Download />
                              )}
                            </Button>
                          )}
                        </td>
                        <td>{movie.relationExplainer}</td>
                        <td>{movie.inspirationDescription}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : formData.output ? (
              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{formData.output}</pre>
            ) : (
              <p className="text-muted mb-0">Output goes here</p>
            )}
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default SandboxPage;
